
/**
 * Taxonomy panel for the sheet mapper.
 *
 * Lets the user:
 *   1. pick a Shopify product-taxonomy category (search OR drill-down),
 *   2. pick a Google product category (same picker),
 *   3. for the chosen Shopify category, toggle each of its attributes on as an
 *      output column and choose the value (Shopify controlled value, a custom
 *      value, or a mapping from a sheet column).
 *
 * Everything is stored on `profile.taxonomy` as full-path category names plus a
 * list of attribute field configs. The mapper/exporter turn that into the
 * "Product Category" / Google column + per-attribute metafield columns
 * (see src/lib/mapper/taxonomy-fields.ts).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  getShopifyCategoryAttributes,
  listGoogleCategories,
  listShopifyCategories,
} from "@/lib/api/endpoints";
import type {
  AttributeValue,
  CategoryAttribute,
  CategoryNode,
} from "@/lib/api/types";
import type {
  Profile,
  ProductType,
  Rule,
  ShopifyRow,
  SourceRow,
  TaxonomyAttributeField,
  TaxonomyConfig,
} from "@/lib/mapper/types";
import { segmentsToEditing } from "@/modules/_shared/fieldInputText";
import { attributeColumnHeader } from "@/lib/mapper/taxonomy-fields";
import { InlineArrow } from "@/components/icons/InlineArrow";
import { MapperInspector } from "./MapperInspector";

/**
 * Context the attribute editor needs to render the FULL module editor
 * (`MapperInspector`) for an attribute value — so attributes support every
 * module (dictionary mapper, conditional if/else, template, AI, …), not just
 * the Shopify/custom/source quick-picks. Supplied by the module drawer; when
 * absent (the kebab panel) attributes fall back to the quick-picks only.
 */
export type AttributeInspectorContext = {
  productType: ProductType;
  sourceRows: SourceRow[];
  previewRows: ShopifyRow[];
  previewLoading: boolean;
  profile: Profile;
  kindMemory: Record<string, Rule>;
  onRememberKind: (key: string, rule: Rule) => void;
};

const inputCls =
  "w-full rounded border border-slate-300 dark:border-border bg-white dark:bg-surface px-2 py-1 text-[12px] focus:border-slate-500 dark:focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600";

function isEmptyRule(rule: Rule | undefined): boolean {
  if (!rule) return true;

  switch (rule.kind) {
    case "static":
    case "template":
      return segmentsToEditing(rule.value ?? "").trim() === "";
    case "source":
      return String(rule.column ?? "").trim() === "";
    case "computed": {
      const args = Array.isArray(rule.args) ? rule.args : [];
      return !args.some((arg) => arg != null && String(arg).trim() !== "");
    }
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Reusable category picker (search + drill-down)
// ---------------------------------------------------------------------------

function CategoryPicker({
  taxonomy,
  label,
  value,
  onChange,
}: {
  taxonomy: "shopify" | "google";
  label: string;
  value?: { categoryId: string; categoryName: string };
  onChange: (sel: { categoryId: string; categoryName: string } | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CategoryNode[]>([]);
  const [loading, setLoading] = useState(false);
  // Breadcrumb of nodes we drilled through (browse mode, when query is empty).
  const [crumbs, setCrumbs] = useState<CategoryNode[]>([]);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parentId = crumbs.length ? crumbs[crumbs.length - 1].id : null;

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = query.trim()
        ? { q: query.trim() }
        : { parent: parentId };
      const json =
        taxonomy === "shopify"
          ? await listShopifyCategories(params)
          : await listGoogleCategories(params);
      setResults(Array.isArray(json) ? json : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [taxonomy, query, parentId]);

  // Debounced refetch whenever the dropdown is open and query/level changes.
  useEffect(() => {
    if (!open) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(fetchList, 200);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [open, fetchList]);

  const select = (n: CategoryNode) => {
    onChange({ categoryId: n.id, categoryName: n.fullName });
    setOpen(false);
    setQuery("");
    setCrumbs([]);
  };

  const browsing = query.trim() === "";

  return (
    <div className="text-[12px]">
      <div className="mb-1 font-semibold text-slate-600 dark:text-slate-400">{label}</div>
      {value ? (
        <div className="flex items-start gap-2">
          <div className="flex-1 rounded border border-slate-200 dark:border-border bg-white dark:bg-surface px-2 py-1 text-slate-800 dark:text-slate-200">
            {value.categoryName}
          </div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="shrink-0 rounded border border-slate-300 dark:border-border px-2 py-1 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
          >
            Change
          </button>
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="shrink-0 rounded border border-slate-300 dark:border-border px-2 py-1 text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-300"
          >
            Clear
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="rounded border border-dashed border-slate-300 dark:border-border px-2 py-1 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
        >
          + Select category
        </button>
      )}

      {open && (
        <div className="mt-2 rounded border border-slate-200 dark:border-border bg-white dark:bg-surface p-2 shadow-sm">
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the full taxonomy…"
            className={inputCls}
          />

          {/* Breadcrumb (browse mode only) */}
          {browsing && (
            <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
              <button
                type="button"
                onClick={() => setCrumbs([])}
                className="hover:text-slate-900 dark:hover:text-slate-100"
              >
                Top
              </button>
              {crumbs.map((c, i) => (
                <span key={c.id} className="flex items-center gap-1">
                  <span className="text-slate-300 dark:text-slate-600">›</span>
                  <button
                    type="button"
                    onClick={() => setCrumbs(crumbs.slice(0, i + 1))}
                    className="hover:text-slate-900 dark:hover:text-slate-100"
                  >
                    {c.name}
                  </button>
                </span>
              ))}
              {crumbs.length > 0 && (
                <button
                  type="button"
                  onClick={() => select(crumbs[crumbs.length - 1])}
                  className="ml-2 rounded bg-slate-900 dark:bg-slate-100 dark:text-slate-950 px-2 py-0.5 text-[10.5px] font-medium text-white hover:bg-slate-700 dark:hover:bg-slate-200"
                >
                  Select “{crumbs[crumbs.length - 1].name}”
                </button>
              )}
            </div>
          )}

          <div className="mt-2 max-h-60 overflow-auto">
            {loading ? (
              <div className="px-2 py-3 text-[11px] text-slate-400 dark:text-slate-500">Loading…</div>
            ) : results.length === 0 ? (
              <div className="px-2 py-3 text-[11px] text-slate-400 dark:text-slate-500">No matches.</div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {results.map((n) => {
                  // In browse mode a category WITH children drills in on click
                  // (expand sub-categories); a leaf selects. "Use" lets you pick
                  // a parent category directly without drilling further.
                  const canDrill = browsing && n.hasChildren;
                  return (
                    <li key={n.id} className="flex items-center gap-1 py-1">
                      <button
                        type="button"
                        onClick={() =>
                          canDrill ? setCrumbs((c) => [...c, n]) : select(n)
                        }
                        className="flex min-w-0 flex-1 items-center gap-1 text-left text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
                        title={
                          canDrill
                            ? `Open “${n.name}” to see sub-categories`
                            : n.fullName
                        }
                      >
                        <span className="truncate">
                          {browsing ? n.name : n.fullName}
                        </span>
                        {canDrill && (
                          <span
                            aria-hidden
                            className="shrink-0 text-slate-300 dark:text-slate-600"
                          >
                            ›
                          </span>
                        )}
                      </button>
                      {canDrill && (
                        <button
                          type="button"
                          onClick={() => select(n)}
                          title={`Use “${n.name}” as the category`}
                          className="shrink-0 rounded border border-slate-200 dark:border-border px-1.5 py-0.5 text-[10.5px] font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
                        >
                          Use
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-attribute value control
// ---------------------------------------------------------------------------

type ValueMode = "shopify" | "custom" | "source";

function deriveMode(rule: Rule | undefined, values: AttributeValue[]): ValueMode {
  if (!rule) return "shopify";
  if (rule.kind === "source") return "source";
  if (rule.kind === "static") {
    const v = segmentsToEditing(rule.value ?? "");
    return values.some((opt) => opt.name === v) ? "shopify" : v ? "custom" : "shopify";
  }
  return "custom";
}

function AttributeRow({
  attr,
  field,
  sourceHeaders,
  inspectorContext,
  onToggle,
  onChangeRule,
}: {
  attr: CategoryAttribute;
  field: TaxonomyAttributeField | undefined;
  sourceHeaders: string[];
  inspectorContext?: AttributeInspectorContext;
  onToggle: (enabled: boolean) => void;
  onChangeRule: (rule: Rule) => void;
}) {
  const enabled = field?.enabled ?? false;
  const rule = field?.rule;
  const mode = deriveMode(rule, attr.values);
  const column = attributeColumnHeader(attr.name, attr.handle);
  const staticValue = rule?.kind === "static" ? segmentsToEditing(rule.value) : "";
  const sourceColumn = rule?.kind === "source" ? rule.column : "";

  // An attribute value can use the FULL module system (dictionary mapper,
  // conditional if/else, template, AI, …), not only the quick picks. If the
  // rule is already an advanced module, show the full editor; otherwise it's
  // available behind "All modules…".
  const isAdvancedRule =
    !!rule && rule.kind !== "static" && rule.kind !== "source";
  const [advanced, setAdvanced] = useState(isAdvancedRule);
  const showAdvanced = (advanced || isAdvancedRule) && Boolean(inspectorContext);

  return (
    <div className="border-t border-slate-100 dark:border-border py-2">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
          <span className="font-medium text-slate-800 dark:text-slate-200">{attr.name}</span>
        </label>
        <span className="text-[10.5px] text-slate-400 dark:text-slate-500">
          {attr.values.length} value{attr.values.length === 1 ? "" : "s"}
        </span>
        {enabled && inspectorContext && (
          <button
            type="button"
            onClick={() => setAdvanced((a) => !a)}
            className="ml-auto rounded border border-slate-200 dark:border-border px-1.5 py-0.5 text-[10.5px] font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-surface-2"
          >
            {showAdvanced ? "Simple options" : "All modules…"}
          </button>
        )}
      </div>

      {enabled &&
        (showAdvanced && inspectorContext ? (
          <div className="mt-2 pl-6">
            <div className="rounded border border-slate-200 dark:border-border bg-slate-50/60 dark:bg-surface-2/40 p-2">
              <MapperInspector
                column={column}
                rule={rule ?? { kind: "static", value: "" }}
                productType={inspectorContext.productType}
                sourceHeaders={sourceHeaders}
                sourceRows={inspectorContext.sourceRows}
                previewRows={inspectorContext.previewRows}
                previewLoading={inspectorContext.previewLoading}
                profile={inspectorContext.profile}
                onChangeRule={onChangeRule}
                onClearRule={() => onChangeRule({ kind: "static", value: "" })}
                kindMemory={inspectorContext.kindMemory}
                onRememberKind={inspectorContext.onRememberKind}
                registerFocusedAutocomplete={() => {}}
              />
            </div>
            <div className="mt-1 text-[10.5px] text-slate-400 dark:text-slate-500">
              <InlineArrow size={12} /> {column}
            </div>
          </div>
        ) : (
          <div className="mt-1.5 grid gap-2 pl-6 md:grid-cols-[140px_1fr]">
            <select
              value={mode}
              onChange={(e) => {
                const m = e.target.value as ValueMode;
                if (m === "shopify") onChangeRule({ kind: "static", value: attr.values[0]?.name ?? "" });
                else if (m === "custom") onChangeRule({ kind: "static", value: staticValue });
                else onChangeRule({ kind: "source", column: sourceColumn || sourceHeaders[0] || "" });
              }}
              className={inputCls}
            >
              <option value="shopify">Shopify value</option>
              <option value="custom">Custom value</option>
              <option value="source">From sheet column</option>
            </select>

            {mode === "shopify" && (
              <select
                value={attr.values.some((v) => v.name === staticValue) ? staticValue : ""}
                onChange={(e) => onChangeRule({ kind: "static", value: e.target.value })}
                className={inputCls}
              >
                <option value="">Choose</option>
                {attr.values.map((v) => (
                  <option key={v.id} value={v.name}>
                    {v.name}
                  </option>
                ))}
              </select>
            )}

            {mode === "custom" && (
              <input
                type="text"
                value={staticValue}
                onChange={(e) => onChangeRule({ kind: "static", value: e.target.value })}
                placeholder="Custom value"
                className={inputCls}
              />
            )}

            {mode === "source" && (
              <select
                value={sourceColumn}
                onChange={(e) => onChangeRule({ kind: "source", column: e.target.value })}
                className={inputCls}
              >
                <option value="">Choose source column</option>
                {sourceHeaders.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            )}

            <div className="text-[10.5px] text-slate-400 dark:text-slate-500 md:col-span-2"><InlineArrow size={12} /> {column}</div>
          </div>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * Reusable taxonomy editor body — the two category pickers + the attribute
 * rows + reconciliation logic. Rendered by both the kebab `TaxonomyPanel` and
 * the module sidebar (ModuleConfigDrawer) so the flow is identical everywhere.
 * No outer chrome (no border/close) — the host supplies that.
 */
export function TaxonomyEditor({
  taxonomy,
  sourceHeaders,
  onChange,
  target = "both",
  inspectorContext,
}: {
  taxonomy: TaxonomyConfig | undefined;
  sourceHeaders: string[];
  onChange: (next: TaxonomyConfig) => void;
  /**
   * Which taxonomy this editor scopes to. "shopify" shows the Shopify category
   * picker + its attributes; "google" shows only the Google category picker;
   * "both" (the kebab panel) shows everything.
   */
  target?: "shopify" | "google" | "both";
  /** When supplied, attribute values can use the full module editor. */
  inspectorContext?: AttributeInspectorContext;
}) {
  const tax: TaxonomyConfig = taxonomy ?? {};
  const showShopify = target !== "google";
  const showGoogle = target !== "shopify";
  const [attributes, setAttributes] = useState<CategoryAttribute[]>([]);
  const [attributesCategoryId, setAttributesCategoryId] = useState<string | undefined>(undefined);

  // Load the selected Shopify category's attributes whenever it changes.
  // When no category is selected the attributes section isn't rendered, so we
  // skip the fetch entirely.
  const shopifyCategoryId = tax.shopify?.categoryId;
  useEffect(() => {
    if (!shopifyCategoryId) return;
    let cancelled = false;

    void getShopifyCategoryAttributes(shopifyCategoryId)
      .then((json) => {
        if (cancelled) return;
        setAttributes(Array.isArray(json) ? json : []);
        setAttributesCategoryId(shopifyCategoryId);
      })
      .catch(() => {
        if (cancelled) return;
        setAttributes([]);
        setAttributesCategoryId(shopifyCategoryId);
      });

    return () => {
      cancelled = true;
    };
  }, [shopifyCategoryId]);

  useEffect(() => {
    if (!shopifyCategoryId || attributesCategoryId !== shopifyCategoryId || attributes.length === 0) return;

    const saved = taxonomy?.attributes ?? [];
    if (saved.length === 0) return;

    const validHandles = new Set(attributes.map((attr) => attr.handle));
    const reconciled = saved.filter(
      (field) => validHandles.has(field.handle) || (field.enabled && !isEmptyRule(field.rule)),
    );

    if (reconciled.length !== saved.length) {
      onChange({ ...(taxonomy ?? {}), attributes: reconciled });
    }
  }, [attributes, attributesCategoryId, onChange, shopifyCategoryId, taxonomy]);

  const fieldFor = (handle: string): TaxonomyAttributeField | undefined =>
    (tax.attributes ?? []).find((a) => a.handle === handle);

  const upsertField = (attr: CategoryAttribute, patch: Partial<TaxonomyAttributeField>) => {
    const existing = fieldFor(attr.handle);
    const base: TaxonomyAttributeField =
      existing ?? { handle: attr.handle, name: attr.name, enabled: false, rule: { kind: "static", value: "" } };
    const merged = { ...base, ...patch };
    const others = (tax.attributes ?? []).filter((a) => a.handle !== attr.handle);
    onChange({ ...tax, attributes: [...others, merged] });
  };

  const validAttributeHandles =
    shopifyCategoryId && attributesCategoryId === shopifyCategoryId && attributes.length > 0
      ? new Set(attributes.map((attr) => attr.handle))
      : undefined;
  const staleFilledAttributes = validAttributeHandles
    ? (tax.attributes ?? []).filter(
        (field) => !validAttributeHandles.has(field.handle) && field.enabled && !isEmptyRule(field.rule),
      )
    : [];
  const showingAttributeLoad = Boolean(tax.shopify) && attributesCategoryId !== shopifyCategoryId;

  return (
    <div>
      <div className={target === "both" ? "grid gap-4 lg:grid-cols-2" : ""}>
        {showShopify && (
          <CategoryPicker
            taxonomy="shopify"
            label="Shopify product category"
            value={tax.shopify}
            onChange={(sel) => onChange({ ...tax, shopify: sel })}
          />
        )}
        {showGoogle && (
          <CategoryPicker
            taxonomy="google"
            label="Google product category"
            value={tax.google}
            onChange={(sel) => onChange({ ...tax, google: sel })}
          />
        )}
      </div>

      {/* Attributes for the chosen Shopify category */}
      {showShopify && tax.shopify && (
        <div className="mt-4">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Attributes, output as metafield columns
          </div>
          <div className="mb-2 text-[11px] text-slate-400 dark:text-slate-500">
            Ticked attributes add an empty column. Fill the value only if you have the data; otherwise leave it
            blank.
          </div>
          {staleFilledAttributes.length > 0 && (
            <div className="mb-2 rounded border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/40 px-2 py-1.5 text-[11px] text-amber-800 dark:text-amber-300">
              <span>
                {staleFilledAttributes.length} attribute column
                {staleFilledAttributes.length === 1 ? "" : "s"} from a previous category{" "}
                {staleFilledAttributes.length === 1 ? "is" : "are"} still active:{" "}
                {staleFilledAttributes.map((field) => field.name || field.handle).join(", ")}.
              </span>{" "}
              <button
                type="button"
                onClick={() => {
                  const staleHandles = new Set(staleFilledAttributes.map((field) => field.handle));
                  onChange({
                    ...tax,
                    attributes: (tax.attributes ?? []).filter((field) => !staleHandles.has(field.handle)),
                  });
                }}
                className="font-medium underline decoration-amber-500/60 underline-offset-2 hover:text-amber-950"
              >
                Remove them
              </button>
            </div>
          )}
          {showingAttributeLoad ? (
            <div className="py-3 text-[11px] text-slate-400 dark:text-slate-500">Loading attributes…</div>
          ) : attributes.length === 0 ? (
            <div className="py-3 text-[11px] text-slate-400 dark:text-slate-500">
              This category has no taxonomy attributes.
            </div>
          ) : (
            <div className="rounded border border-slate-200 dark:border-border bg-white dark:bg-surface px-3 py-1">
              {attributes.map((attr) => (
                <AttributeRow
                  key={attr.id}
                  attr={attr}
                  field={fieldFor(attr.handle)}
                  sourceHeaders={sourceHeaders}
                  inspectorContext={inspectorContext}
                  onToggle={(enabled) => upsertField(attr, { enabled, name: attr.name })}
                  onChangeRule={(rule) => upsertField(attr, { rule, name: attr.name, enabled: true })}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Kebab "Show taxonomy" panel — a top-of-canvas band wrapping {@link TaxonomyEditor}
 * with a header + Close. (The module sidebar renders TaxonomyEditor directly.)
 */
export function TaxonomyPanel({
  taxonomy,
  sourceHeaders,
  onChange,
  onClose,
}: {
  taxonomy: TaxonomyConfig | undefined;
  sourceHeaders: string[];
  onChange: (next: TaxonomyConfig) => void;
  onClose: () => void;
}) {
  return (
    <div className="shrink-0 border-b border-slate-200 dark:border-border bg-slate-50 dark:bg-background px-4 py-3">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Product taxonomy
        </span>
        <button type="button" onClick={onClose} className="text-[11px] text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100">
          Close
        </button>
      </div>
      <TaxonomyEditor taxonomy={taxonomy} sourceHeaders={sourceHeaders} onChange={onChange} />
    </div>
  );
}
